import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSettings } from '../user/util/SettingsProvider';
export const OfferGo = () => {
  const { trackId } = useParams();
  const navigate = useNavigate();

  // Page States
  const [offerData, setOfferData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Accordion Toggles
  const [isStepsOpen, setIsStepsOpen] = useState(true);
  const [isTncOpen, setIsTncOpen] = useState(true);

  // Form Submission States
  const [upiId, setUpiId] = useState('');
  const [dynamicInputs, setDynamicInputs] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Modal States
  const [isTrackModalOpen, setIsTrackModalOpen] = useState(false);
  const [searchTrackId, setSearchTrackId] = useState('');
  const [leadStatusResult, setLeadStatusResult] = useState(null);
  const [isSearchingLead, setIsSearchingLead] = useState(false);

  // Offer Status & Redirect Modals
  const [isRedirectModalOpen, setIsRedirectModalOpen] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
  const [isNotFoundModalOpen, setIsNotFoundModalOpen] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const settings = useSettings();
  useEffect(() => { document.title = `Offer | ${settings?.settings?.site_name || ''}`; }, [settings?.settings?.site_name]);
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  // Generate lightweight browser fingerprint using CDN / native web signals
  const getBrowserFingerprint = () => {
    try {
      const nav = window.navigator;
      const screen = window.screen;
      const str = `${nav.userAgent}|${nav.language}|${screen.colorDepth}|${screen.width}x${screen.height}|${new Date().getTimezoneOffset()}`;

      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
      }
      return Math.abs(hash).toString(16) + 'dcf9e6';
    } catch (e) {
      return 'dcf9e6daaade3f8c652e55599596750b';
    }
  };

  // Helper function to extract Telegram link from localStorage settings
  const getFallbackTelegramLink = () => {
    try {
      const settings = JSON.parse(localStorage.getItem('settings') || '{}');
      return settings.tgLink || 'https://t.me';
    } catch (e) {
      return 'https://t.me';
    }
  };

  // Fetch Offer Details
  useEffect(() => {
    const fetchOfferTrackingDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = getCookie('authToken');
        const baseUrl = process.env.REACT_APP_API_URL || '';

        const response = await axios.get(`${baseUrl}/api/public/track/${trackId}`, {
          params: { trackId: trackId },
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        const resData = response.data?.data || response.data;

        if (resData) {
          // Check for inactive status
          const status = String(resData.status || '').toLowerCase();
          if (status === 'inactive' || resData.isActive === false) {
            setIsInactiveModalOpen(true);
            const channelUrl = resData.channel || getFallbackTelegramLink();
            let count = 5;
            const timer = setInterval(() => {
              count -= 1;
              setRedirectCountdown(count);
              if (count <= 0) {
                clearInterval(timer);
                window.location.href = channelUrl;
              }
            }, 1000);
            setLoading(false);
            return;
          }

          setOfferData(resData);

          // Pre-populate dynamic input fields based on required_inputs
          if (resData.required_inputs) {
            const inputsArr = String(resData.required_inputs).split(',').map(s => s.trim());
            const initialInputs = {};
            inputsArr.forEach(label => {
              if (label) initialInputs[label] = '';
            });
            setDynamicInputs(initialInputs);
          }
        } else {
          handleOfferNotFound();
        }
      } catch (err) {
        console.error('Error fetching offer tracking details:', err);
        handleOfferNotFound();
      } finally {
        setLoading(false);
      }
    };

    const handleOfferNotFound = () => {
      setIsNotFoundModalOpen(true);
      const tgLink = getFallbackTelegramLink();
      let count = 5;
      const timer = setInterval(() => {
        count -= 1;
        setRedirectCountdown(count);
        if (count <= 0) {
          clearInterval(timer);
          window.location.href = tgLink;
        }
      }, 1000);
    };

    fetchOfferTrackingDetails();
  }, [trackId]);

  // Handle Dynamic Input Changes
  const handleDynamicInputChange = (label, value) => {
    setDynamicInputs(prev => ({
      ...prev,
      [label]: value
    }));
  };

  // Handle Form Submission (Claim Offer)
  const handleSubmitLead = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!upiId || !upiId.includes('@')) {
      setSubmitError('Please enter a valid UPI ID (e.g., name@upi).');
      return;
    }

    try {
      setIsSubmitting(true);
      const token = getCookie('authToken');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const payload = {
        trackId: offerData?.trackingData?.trackId || trackId,
        upi: upiId,
        fingerprint: getBrowserFingerprint(),
        extraInputs: dynamicInputs
      };

      const response = await axios.post(`${baseUrl}/api/public/submit-click`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      const resData = response.data;

      if (resData?.status) {
        setSubmitSuccess(true);
        const destination = resData?.redirect || offerData?.trackLink;

        if (destination) {
          setRedirectUrl(destination);
          setIsRedirectModalOpen(true);
          let count = 3;
          const timer = setInterval(() => {
            count -= 1;
            setRedirectCountdown(count);
            if (count <= 0) {
              clearInterval(timer);
              window.location.href = destination;
            }
          }, 1000);
        }
      } else {
        setSubmitError(resData?.msg || 'Failed to record click. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting lead:', err);
      setSubmitError(err.response?.data?.msg || err.response?.data?.message || 'Failed to submit claim. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Track Lead Search
  const handleSearchLeadStatus = async (e) => {
    e.preventDefault();
    if (!searchTrackId.trim()) return;

    try {
      setIsSearchingLead(true);
      setLeadStatusResult(null);
      const token = getCookie('authToken');
      const baseUrl = process.env.REACT_APP_API_URL || '';

      const response = await axios.get(`${baseUrl}/api/offers/lead-status/${searchTrackId.trim()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setLeadStatusResult(response.data?.data || response.data);
    } catch (err) {
      console.error('Error tracking lead status:', err);
      setLeadStatusResult({ error: 'No record found for this Reference / Track ID.' });
    } finally {
      setIsSearchingLead(false);
    }
  };

  // Parsing steps & terms into arrays
  const stepsList = offerData?.steps
    ? (Array.isArray(offerData.steps) ? offerData.steps : String(offerData.steps).split(','))
    : [];

  const tncList = offerData?.tnc
    ? (Array.isArray(offerData.tnc) ? offerData.tnc : String(offerData.tnc).split(','))
    : [];

  const trackIdDisplay = offerData?.trackingData?.trackId || trackId || 'fa48dbbe4c74';

  return (
    <div className="w-full bg-zinc-950 text-zinc-100 pt-6 sm:pt-12 lg:pt-16 pb-24 px-4 sm:px-8 relative overflow-hidden font-sans min-h-screen selection:bg-emerald-500 selection:text-zinc-950">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }

        @keyframes pulseGlow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.45; transform: scale(1.1); }
        }

        @keyframes borderRotate {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        @keyframes floatEffect {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }

        @keyframes scaleIn {
          0% { opacity: 0; transform: scale(0.94); }
          100% { opacity: 1; transform: scale(1); }
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .animate-spring-in {
          animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .animate-scale-in {
          animation: scaleIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .ambient-glow {
          animation: pulseGlow 7s ease-in-out infinite;
        }

        .highlight-border {
          background: linear-gradient(135deg, rgba(16, 185, 129, 0.6) 0%, rgba(24, 24, 27, 0.9) 40%, rgba(20, 184, 166, 0.6) 100%);
          background-size: 200% 200%;
          animation: borderRotate 5s ease infinite;
        }

        .highlight-card-glow {
          box-shadow: 0 0 35px -5px rgba(16, 185, 129, 0.25), 0 20px 25px -5px rgba(0, 0, 0, 0.5);
        }

        .smooth-transition {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }

        .float-animation {
          animation: floatEffect 4s ease-in-out infinite;
        }

        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 8px;
        }
      `}</style>

      {/* Ambient background blur circles */}
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-emerald-500/15 rounded-full blur-[140px] pointer-events-none ambient-glow" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-500/10 rounded-full blur-[120px] pointer-events-none ambient-glow" />

      <div className="max-w-[850px] mx-auto space-y-8 relative z-10">

        {/* ========== SKELETON LOADING ========== */}
        {loading && (
          <div className="space-y-8 animate-scale-in">
            {/* Skeleton: Offer Details Card */}
            <div className="relative p-[2px] rounded-[2.5rem] bg-zinc-800/60">
              <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 sm:p-10 rounded-[2.4rem] relative z-10 space-y-8">
                {/* Logo + Name skeleton */}
                <div className="flex flex-row items-center gap-5 sm:gap-7">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-zinc-800 rounded-[1.8rem] shimmer-bg shrink-0" />
                  <div className="flex-1 space-y-3">
                    <div className="w-28 h-6 bg-zinc-800 rounded-full shimmer-bg" />
                    <div className="w-52 h-8 bg-zinc-800 rounded-lg shimmer-bg" />
                    <div className="flex gap-6 pt-1">
                      <div className="space-y-1">
                        <div className="w-14 h-3 bg-zinc-800 rounded shimmer-bg" />
                        <div className="w-20 h-7 bg-zinc-800 rounded-md shimmer-bg" />
                      </div>
                      <div className="space-y-1">
                        <div className="w-20 h-3 bg-zinc-800 rounded shimmer-bg" />
                        <div className="w-28 h-7 bg-zinc-800 rounded-md shimmer-bg" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Form skeleton */}
                <div className="p-6 sm:p-8 bg-zinc-950/90 rounded-[2rem] border border-zinc-800/80 space-y-5">
                  <div className="space-y-2">
                    <div className="w-44 h-6 bg-zinc-800 rounded shimmer-bg" />
                    <div className="w-72 h-4 bg-zinc-800 rounded shimmer-bg" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="w-full h-13 bg-zinc-800 rounded-xl shimmer-bg" />
                    <div className="w-full h-13 bg-zinc-800 rounded-xl shimmer-bg" />
                  </div>
                  <div className="w-full h-14 bg-zinc-800 rounded-xl shimmer-bg" />
                </div>
              </div>
            </div>

            {/* Skeleton: Accordion 1 */}
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden">
              <div className="px-6 sm:px-8 py-5 flex items-center gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-xl shimmer-bg" />
                <div className="w-28 h-4 bg-zinc-800 rounded shimmer-bg" />
              </div>
              <div className="px-6 sm:px-8 pb-8 space-y-3.5 border-t border-zinc-800/50 pt-4">
                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
                <div className="w-full h-12 bg-zinc-800/60 rounded-2xl shimmer-bg" />
              </div>
            </div>

            {/* Skeleton: Accordion 2 */}
            <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden">
              <div className="px-6 sm:px-8 py-5 flex items-center gap-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-xl shimmer-bg" />
                <div className="w-40 h-4 bg-zinc-800 rounded shimmer-bg" />
              </div>
              <div className="px-6 sm:px-8 pb-8 space-y-3 border-t border-zinc-800/50 pt-4">
                <div className="w-full h-4 bg-zinc-800/60 rounded shimmer-bg" />
                <div className="w-5/6 h-4 bg-zinc-800/60 rounded shimmer-bg" />
                <div className="w-4/6 h-4 bg-zinc-800/60 rounded shimmer-bg" />
              </div>
            </div>
          </div>
        )}

        {/* ========== ERROR STATE ========== */}
        {!loading && error && (
          <div className="text-center py-20 bg-zinc-900/80 rounded-[2.5rem] border border-zinc-800 backdrop-blur-2xl animate-spring-in space-y-4 px-6">
            <div className="w-14 h-14 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center mx-auto text-2xl">
              ⚠️
            </div>
            <p className="text-zinc-200 text-base font-semibold">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-100 rounded-xl text-xs font-bold border border-zinc-700 smooth-transition micro-interaction"
            >
              Retry
            </button>
          </div>
        )}

        {/* ========== OFFER CONTENT ========== */}
        {!loading && !error && offerData && (
          <div className="space-y-8 animate-scale-in">

            {/* 1. HIGHLIGHTED OFFER DETAILS SECTION */}
            <div className="relative p-[2px] rounded-[2.5rem] highlight-border highlight-card-glow smooth-transition group">
              <div className="bg-zinc-900/95 backdrop-blur-2xl p-6 sm:p-10 rounded-[2.4rem] relative z-10 space-y-8">

                {/* Offer Logo Beside Offer Name (Horizontal Layout) */}
                <div className="flex flex-row items-center gap-5 sm:gap-7 text-left">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-zinc-950 rounded-[1.8rem] border border-emerald-500/30 flex items-center justify-center shadow-inner shrink-0 overflow-hidden float-animation group-hover:border-emerald-400 smooth-transition">
                    {offerData?.logo ? (
                      <img
                        src={offerData.logo}
                        alt={offerData?.name || 'Campaign'}
                        className="w-full h-full object-cover rounded-[1.8rem]"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(offerData?.name || 'Offer') + '&background=059669&color=fff';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full bg-zinc-800/50 rounded-[1.8rem] animate-pulse" />
                    )}
                  </div>

                  <div className="flex-1 space-y-2 min-w-0">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Verified Offer
                    </div>

                    <h1 className="text-2xl sm:text-3xl font-black text-zinc-100 tracking-tight leading-tight group-hover:text-emerald-400 smooth-transition truncate">
                      {offerData?.name || 'Loading offer details...'}
                    </h1>

                    <div className="flex flex-wrap items-center gap-4 sm:gap-6 pt-1">
                      <div className="border-r border-zinc-800 pr-4 sm:pr-6">
                        <p className="text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest">Payout</p>
                        <p className="text-xl sm:text-2xl font-black text-emerald-400 mt-0.5">₹{offerData?.payout || 0}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-extrabold text-zinc-500 uppercase tracking-widest">Track Reference</p>
                        <p className="text-xs sm:text-sm font-mono font-bold text-zinc-300 truncate max-w-[140px] mt-1 bg-zinc-950 px-2.5 py-1 rounded-lg border border-zinc-800/80">
                          #{trackIdDisplay}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CLAIM FORM */}
                <form onSubmit={handleSubmitLead} className="p-6 sm:p-8 bg-zinc-950/90 rounded-[2rem] border border-zinc-800/80 shadow-2xl relative overflow-hidden group/form">
                  <div className="absolute top-0 right-0 p-8 opacity-5 group-hover/form:scale-110 smooth-transition pointer-events-none">
                    <svg className="w-32 h-32 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>

                  <div className="relative z-10 space-y-5">
                    <div>
                      <h3 className="text-zinc-100 text-lg font-black tracking-tight">Get Started Instantly</h3>
                      <p className="text-zinc-400 text-xs font-medium mt-1">
                        Provide required details below to track your referral &amp; receive payment after verification.
                      </p>
                    </div>

                    {submitError && (
                      <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-400 text-xs font-medium flex items-center gap-2 animate-spring-in">
                        <span className="shrink-0">⚠️</span>
                        <span>{submitError}</span>
                      </div>
                    )}

                    {submitSuccess ? (
                      <div className="py-8 text-center space-y-3 bg-emerald-500/10 rounded-2xl border border-emerald-500/20 animate-spring-in">
                        <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto text-xl">
                          ✓
                        </div>
                        <h4 className="text-lg font-black text-zinc-100">Claim Details Recorded!</h4>
                        <p className="text-xs text-zinc-400 max-w-sm mx-auto">
                          Redirecting you to the offer landing page... Complete the required steps to get credited.
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                          {/* Standard UPI ID Input */}
                          <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-400">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                            </div>
                            <input
                              required
                              type="text"
                              placeholder="UPI ID (example@apl)"
                              value={upiId}
                              onChange={(e) => setUpiId(e.target.value)}
                              className="w-full pl-11 pr-4 py-3.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-100 font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 smooth-transition text-xs placeholder:text-zinc-600"
                            />
                          </div>

                          {/* Dynamic Required Inputs from Backend */}
                          {Object.keys(dynamicInputs).map((inputLabel, idx) => (
                            <div key={idx} className="relative">
                              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-teal-400">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                              </div>
                              <input
                                required
                                type="text"
                                placeholder={inputLabel}
                                value={dynamicInputs[inputLabel]}
                                onChange={(e) => handleDynamicInputChange(inputLabel, e.target.value)}
                                className="w-full pl-11 pr-4 py-3.5 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-100 font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 smooth-transition text-xs placeholder:text-zinc-600"
                              />
                            </div>
                          ))}

                        </div>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 smooth-transition micro-interaction disabled:opacity-50"
                        >
                          {isSubmitting ? (
                            <span>Processing...</span>
                          ) : (
                            <>
                              <span>Claim Now</span>
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                              </svg>
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </form>

              </div>
            </div>

            {/* 2. HOW TO APPLY ACCORDION */}
            {stepsList.length > 0 && (
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden shadow-2xl animate-spring-in smooth-transition hover:border-zinc-700">
                <button
                  onClick={() => setIsStepsOpen(!isStepsOpen)}
                  className="w-full px-6 sm:px-8 py-5 flex items-center justify-between hover:bg-zinc-800/40 smooth-transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                    <span className="font-black text-xs uppercase tracking-widest text-zinc-200">How to Apply</span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-zinc-400 smooth-transition ${isStepsOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isStepsOpen && (
                  <div className="px-6 sm:px-8 pb-8 space-y-3.5 border-t border-zinc-800/50 pt-4">
                    {stepsList.map((step, idx) => (
                      <div key={idx} className="flex items-start gap-4 group/step p-3.5 bg-zinc-950/60 rounded-2xl border border-zinc-800/60 hover:border-emerald-500/40 smooth-transition micro-interaction">
                        <div className="w-6 h-6 rounded-xl bg-zinc-900 border border-zinc-800 text-emerald-400 group-hover/step:bg-emerald-500 group-hover/step:text-zinc-950 group-hover/step:border-emerald-400 text-[11px] font-black flex items-center justify-center shrink-0 smooth-transition">
                          {idx + 1}
                        </div>
                        <p className="text-xs font-semibold text-zinc-300 leading-relaxed pt-0.5">{step.trim()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 3. TERMS & CONDITIONS ACCORDION */}
            {tncList.length > 0 && (
              <div className="bg-zinc-900/80 backdrop-blur-xl border border-zinc-800/80 rounded-[2.5rem] overflow-hidden shadow-2xl animate-spring-in smooth-transition hover:border-zinc-700">
                <button
                  onClick={() => setIsTncOpen(!isTncOpen)}
                  className="w-full px-6 sm:px-8 py-5 flex items-center justify-between hover:bg-zinc-800/40 smooth-transition"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-xl flex items-center justify-center shrink-0">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <span className="font-black text-xs uppercase tracking-widest text-zinc-200">Terms &amp; Conditions</span>
                  </div>
                  <svg
                    className={`w-5 h-5 text-zinc-400 smooth-transition ${isTncOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {isTncOpen && (
                  <div className="px-6 sm:px-8 pb-8 space-y-3 border-t border-zinc-800/50 pt-4">
                    {tncList.map((term, idx) => (
                      <div key={idx} className="flex items-start gap-3 smooth-transition micro-interaction">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-2 shrink-0"></div>
                        <p className="text-xs font-medium text-zinc-400 italic leading-relaxed">{term.trim()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

          </div>
        )}

      </div>

      {/* FLOATING TRACK LEAD BUTTON — navigates to /track/trackId */}
      <div className="fixed bottom-6 right-6 z-40">
        <button
          onClick={() => navigate(`/track/${trackId}`)}
          className="flex items-center gap-2.5 px-5 py-3.5 bg-zinc-900 border border-zinc-700/80 hover:border-emerald-500/50 text-zinc-100 rounded-full font-black text-xs uppercase tracking-widest shadow-2xl shadow-black hover:bg-zinc-800 smooth-transition micro-interaction group"
        >
          <svg className="w-4 h-4 text-emerald-400 group-hover:rotate-12 smooth-transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          Track Lead
        </button>
      </div>

      {/* TRACK LEAD SEARCH MODAL */}
      {isTrackModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-[2.5rem] shadow-2xl overflow-hidden p-6 sm:p-8 space-y-6">

            <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
              <div>
                <h3 className="text-lg font-black text-zinc-100">Check Lead Status</h3>
                <p className="text-xs text-zinc-400 font-medium">Verify tracking status using your reference ID.</p>
              </div>
              <button
                onClick={() => {
                  setIsTrackModalOpen(false);
                  setLeadStatusResult(null);
                }}
                className="p-2 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-xl smooth-transition micro-interaction"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSearchLeadStatus} className="space-y-4">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 block mb-1.5">
                  Reference / Track ID
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. fa48dbbe4c74"
                  value={searchTrackId}
                  onChange={(e) => setSearchTrackId(e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-950 border border-zinc-800 rounded-xl text-zinc-100 font-mono text-xs outline-none focus:border-emerald-500/50 smooth-transition"
                />
              </div>

              <button
                type="submit"
                disabled={isSearchingLead}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-emerald-500/20 disabled:opacity-50"
              >
                {isSearchingLead ? 'Searching...' : 'Search Status'}
              </button>
            </form>

            {/* Search Result */}
            {leadStatusResult && (
              <div className="pt-2 border-t border-zinc-800 animate-spring-in">
                {leadStatusResult.error ? (
                  <p className="text-xs font-semibold text-rose-400 bg-rose-500/10 p-3 rounded-xl border border-rose-500/20 text-center">
                    {leadStatusResult.error}
                  </p>
                ) : (
                  <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800 space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-zinc-500 font-bold uppercase">Status:</span>
                      <span className="px-2.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-full font-bold uppercase text-[10px]">
                        {leadStatusResult.status || 'Pending Verification'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-zinc-500 font-bold uppercase">Payout:</span>
                      <span className="text-emerald-400 font-bold">₹{leadStatusResult.payout || offerData?.payout || 0}</span>
                    </div>
                    {leadStatusResult.updatedAt && (
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-zinc-500 font-bold uppercase">Updated:</span>
                        <span className="text-zinc-400 font-mono text-[10px]">{leadStatusResult.updatedAt}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      )}

      {/* SUCCESS SUBMISSION / REDIRECT MODAL */}
      {isRedirectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-emerald-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6 highlight-card-glow">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto float-animation">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
              </svg>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Claim Recorded Successfully!</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                Your tracking details have been verified. Redirecting you to the offer destination now...
              </p>
            </div>

            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800 space-y-3">
              <div className="flex items-center justify-center gap-2 text-emerald-400 text-xs font-bold">
                <div className="w-4 h-4 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Redirecting in {redirectCountdown}s</span>
              </div>
            </div>

            <button
              onClick={() => window.location.href = redirectUrl}
              className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2"
            >
              <span>Continue Immediately</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* OFFER INACTIVE MODAL */}
      {isInactiveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-amber-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20 flex items-center justify-center mx-auto float-animation">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Offer Currently Inactive</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                This campaign is temporarily paused or completed. Redirecting you to our official channel for active updates...
              </p>
            </div>

            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
              <p className="text-amber-400 text-xs font-mono font-bold">
                Redirecting to Channel in {redirectCountdown}s
              </p>
            </div>

            <button
              onClick={() => window.location.href = offerData?.channel || getFallbackTelegramLink()}
              className="w-full py-3.5 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
            >
              <span>Go to Channel Now</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* OFFER NOT FOUND MODAL */}
      {isNotFoundModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-spring-in">
          <div className="relative w-full max-w-md bg-zinc-900 border border-rose-500/30 rounded-[2.5rem] shadow-2xl overflow-hidden p-8 text-center space-y-6">
            <div className="w-16 h-16 rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center mx-auto float-animation">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight">Offer Not Found</h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                The tracking link you accessed could not be resolved or has expired. Redirecting you to our updates channel...
              </p>
            </div>

            <div className="p-4 bg-zinc-950 rounded-2xl border border-zinc-800">
              <p className="text-rose-400 text-xs font-mono font-bold">
                Redirecting in {redirectCountdown}s
              </p>
            </div>

            <button
              onClick={() => window.location.href = getFallbackTelegramLink()}
              className="w-full py-3.5 bg-rose-500 hover:bg-rose-400 text-zinc-950 font-black rounded-xl text-xs uppercase tracking-wider smooth-transition micro-interaction shadow-lg shadow-rose-500/20 flex items-center justify-center gap-2"
            >
              <span>Join Updates Channel</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </button>
          </div>
        </div>
      )}

    </div>
  );
};

export default OfferGo;